import {AfterViewInit, Component, DestroyRef, OnDestroy, OnInit, inject} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {DomSanitizer} from '@angular/platform-browser';
import {BreadcrumbService} from 'xng-breadcrumb';
import {AllService} from '../../../all/all.service';
import {environment} from '../../../../environments/environment';
import {Globals} from '../../../globals';
import {
  applyAddListingTaxonomyBreadcrumbs,
  applyBrowseTaxonomyBreadcrumbs,
  canonicalAddListingRouterUrl,
  clearAddListingBrowseFilters,
  globalsAddListingFilter,
  globalsBrowseFilter,
  isProfileUrl,
  parseAddListingTaxonomySegments,
  resolveBaseCategory,
} from '../../../shared/listing-route.utils';
import {canonicalBrowseRouterUrl} from '../../../routing/legacy-browse-url';
import {FileUploadService} from '../../../add-item/file-upload.service';
import {TokenStorageService} from '../../../_services/token-storage.service';
import {
  CategoryContext,
  CategoryLevelConfig,
  CategoryLevelRouteData,
  FILTER_CHAIN,
  getCategoryLevelConfig,
  getSaveCategoryFieldName,
} from './category-level.config';
import {
  addListingItemsApiPath,
  buildAddItemLink,
  buildAddListingCategoryApiParamsFromUrl,
  buildBrowseCategoryApiParams,
  buildBrowseCategoryLink,
  addListingCategoryPickerDepth,
  browseCategoryPickerDepth,
  isCategoryModule,
  categoryAllCardTitle,
  categoryDisplayTitle,
  categoryItemCountLabel,
  getGroupTitle,
  shouldShowAllCard,
} from './category-level.utils';
import {filter, Subscription} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SeoService} from '../../../shared/seo.service';
import {
  browsePathSegments,
  buildBrowseSeoMeta,
  formatBrowseSegmentLabel,
  sumCategoryItemCounts,
} from '../../../shared/browse-seo.utils';
import {resolveCategoryImageUrl} from '../../../shared/category-visual/category-visual.utils';

type CategoryForm = { name: string; url: string; module: string; svg: string };

@Component({
  standalone: false,
  selector: 'app-category-level',
  templateUrl: './category-level.component.html',
  styleUrls: ['./category-level.component.scss'],
})
export class CategoryLevelComponent implements OnInit, AfterViewInit, OnDestroy {
  url = '';
  imageSrc = '';
  urls: string[] = [];
  urlName = '';
  itemsShop: any[] = [];
  id: string | undefined;
  showModal = false;
  depth = 2;
  context: CategoryContext = 'browse';
  config!: CategoryLevelConfig;
  currentUser: any;
  categoryForms: Record<string, CategoryForm> = {};
  private routeSubscription?: Subscription;
  private itemsRequestId = 0;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private token: TokenStorageService,
    private fileService: FileUploadService,
    private router: Router,
    private allService: AllService,
    private breadcrumbService: BreadcrumbService,
    public sanitizer: DomSanitizer,
    public globals: Globals,
    private seoService: SeoService,
  ) {
    environment.then(env => {
      this.url = env['backendURL'];
      this.imageSrc = env['images'] || '';
    });
  }

  ngOnInit(): void {
    const routeData = this.route.snapshot.data['categoryLevel'] as CategoryLevelRouteData | undefined;
    this.depth = routeData?.depth ?? 2;
    this.context = routeData?.context ?? 'browse';
    this.config = getCategoryLevelConfig(this.depth, this.context);
    this.initCategoryForms();
    this.currentUser = this.token.getUser();

    if (this.isCategoryBrowse) {
      sessionStorage.removeItem('listing_filter_snapshot');
      sessionStorage.removeItem('sectionState');
      localStorage.removeItem('listing_form_values');
    }

    this.allService.getCounties(this.url, '/counties').subscribe();

    this.routeSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe((event) => this.syncFromRoute(event.urlAfterRedirects || event.url));

    // Defer the first sync until the router URL reflects the activated route.
    queueMicrotask(() => this.syncFromRoute(this.router.url));
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    if (this.isCategoryBrowse) {
      this.seoService.clearBrowsePage();
    }
  }

  private syncFromRoute(pathUrl?: string): void {
    const currentPath = (pathUrl ?? this.router.url).split(/[?#]/)[0];

    if (this.context === 'add-item') {
      const canonical = canonicalAddListingRouterUrl(pathUrl ?? this.router.url);
      if (canonical) {
        void this.router.navigateByUrl(canonical, {replaceUrl: true});
        return;
      }
    }

    if (this.isCategoryBrowse) {
      const browseCanonical = canonicalBrowseRouterUrl(pathUrl ?? this.router.url);
      if (browseCanonical) {
        void this.router.navigateByUrl(browseCanonical, {replaceUrl: true});
        return;
      }
    }

    if (this.isCategoryBrowse && this.depth === 2) {
      sessionStorage.removeItem('listing_filter_snapshot');
      sessionStorage.removeItem('sectionState');
      localStorage.removeItem('listing_form_values');
    }

    const segments = currentPath.split('/').filter(Boolean);
    if (this.isCategoryBrowse && segments[0] === 'add-listing') {
      return;
    }
    if (this.context === 'add-item' && segments[0] !== 'add-listing') {
      return;
    }
    if (this.isCategoryBrowse && browseCategoryPickerDepth(segments) !== this.depth) {
      return;
    }

    if (isProfileUrl(segments)) {
      this.id = '/' + segments[0];
    } else {
      const profileId = this.route.snapshot.paramMap.get('id');
      if (profileId) {
        this.id = '/' + profileId;
      }
    }

    this.urls = segments;
    if (this.context === 'add-item') {
      clearAddListingBrowseFilters(this.globals);
      this.urlName = globalsAddListingFilter(this.urls, this.globals);
    } else {
      this.clearStaleBrowseFilters();
      this.urlName = globalsBrowseFilter(this.urls, this.globals);
    }
    this.setBreadcrumb();
    this.loadItems();
  }

  ngAfterViewInit(): void {
    if (this.isCategoryBrowse && this.depth === 2) {
      setTimeout(() => this.setBreadcrumb(), 500);
    }
  }

  get groupTitle(): string {
    if (this.context === 'add-item') {
      if (this.pickerDepth === 2) {
        return getGroupTitle(this.globals, 'category');
      }
      if (this.pickerDepth >= 3) {
        return getGroupTitle(this.globals, FILTER_CHAIN[this.pickerDepth - 3]);
      }
    }
    return getGroupTitle(this.globals, this.config.titleFilterKey);
  }

  get parentCategoryTitle(): string {
    const value = `${this.globals.listingFilter.subcategory ?? ''}`;
    return value && value !== 'null' ? value : '';
  }

  get newCategory(): CategoryForm {
    return this.categoryForms[this.config.newCategoryField];
  }

  get newCategoryLabel(): string {
    return `New ${'Sub'.repeat(this.depth)}category`;
  }

  itemCountLabel(item: any[]): string {
    return categoryItemCountLabel(item);
  }

  allCardTitle(item: any[]): string {
    return categoryAllCardTitle(item);
  }

  displayTitle(item: any[]): string {
    return categoryDisplayTitle(item);
  }

  /** Optional popular-listing / uploaded preview at row index 5 when API provides it. */
  categoryImageUrl(item: any[]): string | null {
    return resolveCategoryImageUrl(item, this.imageSrc);
  }

  isModule(item: any[]): boolean {
    return isCategoryModule(item);
  }

  get sectionTitle(): string {
    const label = formatBrowseSegmentLabel(this.groupTitle || '');
    if (this.context === 'add-item') {
      return label ? `${label} Categories` : 'Categories';
    }
    return label ? `${label} Categories` : 'Categories';
  }

  get pickerDepth(): number {
    const segments = (this.urls.length ? this.urls : this.router.url.split(/[?#]/)[0].split('/').filter(Boolean));
    if (this.context === 'add-item') {
      return addListingCategoryPickerDepth(segments);
    }
    return this.depth;
  }

  get cardLayoutDepth(): number {
    // Route depth drives card chrome. pickerDepth can briefly be 1 while the
    // router URL catches up after selecting a base category — that used to hide
    // every card (template only renders for depth === 2 or depth > 2).
    if (this.context === 'add-item') {
      return Math.max(this.pickerDepth, this.depth);
    }
    return this.depth;
  }

  get isCategoryBrowse(): boolean {
    return this.context === 'browse';
  }

  get baseCategory(): string {
    return resolveBaseCategory(this.router.url.split('/').filter(Boolean));
  }

  buildLink(item: any[], isAllCard: boolean): any[] {
    if (this.context === 'add-item') {
      return buildAddItemLink(this.router.url, item, isAllCard, this.baseCategory);
    }
    return buildBrowseCategoryLink(this.id, this.globals, this.depth, item, isAllCard, this.baseCategory);
  }

  showAllCard(item: any[], index: number): boolean {
    if (this.context === 'add-item') {
      return false;
    }
    return shouldShowAllCard(this.depth, index, item, this.baseCategory);
  }

  openReportModal(): void {
    this.showModal = true;
  }

  closeReportModal(): void {
    this.showModal = false;
  }

  saveCategory(): void {
    const obj: any = {
      category: this.baseCategory,
      username: this.currentUser.username,
    };

    for (let i = 1; i <= this.depth; i++) {
      const fieldName = getSaveCategoryFieldName(i);
      if (i < this.depth) {
        this.categoryForms[fieldName].name = `${this.globals.listingFilter[FILTER_CHAIN[i - 1]] ?? ''}`;
      }
      obj[fieldName] = this.categoryForms[fieldName];
    }

    this.fileService.saveCategory(this.url + '/api/add-category/save', obj).subscribe();
  }

  resetForm(): void {
    this.initCategoryForms();
  }

  private initCategoryForms(): void {
    this.categoryForms = {};
    for (let i = 1; i <= this.depth; i++) {
      const fieldName = getSaveCategoryFieldName(i);
      this.categoryForms[fieldName] = {name: '', url: '', module: '', svg: ''};
    }
  }

  private loadItems(): void {
    if (this.id == null) {
      this.id = '';
    } else if (this.context !== 'add-item' && this.urls[0] !== 'add-listing') {
      this.globals.listingFilter.profile = this.id.replace('/', '');
    }

    const itemsApiPath = this.context === 'add-item'
      ? addListingItemsApiPath(this.router.url)
      : this.config.itemsApiPath;
    const params = this.context === 'add-item'
      ? buildAddListingCategoryApiParamsFromUrl(this.urls)
      : buildBrowseCategoryApiParams(this.urls, this.depth);
    // Depth-2+ add-listing pickers need the base category from the URL; skip the
    // fetch until sync catches up so we don't paint root categories (or blanks).
    if (this.context === 'add-item' && this.depth >= 2 && !params.category) {
      return;
    }
    const requestId = ++this.itemsRequestId;
    this.itemsShop = [];
    const pathOnly = this.router.url.split(/[?#]/)[0];

    const fetchItems = () => {
      this.allService.getItemsCategories(this.url + itemsApiPath, params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(response => {
          if (requestId !== this.itemsRequestId) {
            return;
          }
          this.itemsShop = response;
          this.maybeRedirectAddListingLeafForm(pathOnly);
          this.maybeRedirectBrowseLeafListings(pathOnly);
          if (this.isCategoryBrowse) {
            this.applyBrowseSeo();
          }
        });
    };

    if (this.url) {
      fetchItems();
      return;
    }

    environment.then(env => {
      this.url = env['backendURL'];
      if (requestId !== this.itemsRequestId) {
        return;
      }
      fetchItems();
    });
  }

  private setBreadcrumb(): void {
    if (this.context === 'add-item') {
      applyAddListingTaxonomyBreadcrumbs(this.breadcrumbService, this.router.url);
      return;
    }

    applyBrowseTaxonomyBreadcrumbs(this.breadcrumbService, this.router.url);
    const urlname = formatBrowseSegmentLabel(this.urlName || '');
    this.applyBrowseSeo(urlname);
  }

  private applyBrowseSeo(leafLabel?: string): void {
    if (!this.isCategoryBrowse) {
      return;
    }
    const segments = this.urls?.length
      ? this.urls
      : browsePathSegments(this.router.url);
    const label = (leafLabel || '').trim()
      || formatBrowseSegmentLabel(this.urlName || segments[segments.length - 1] || 'Browse');
    const children = Array.isArray(this.itemsShop) ? this.itemsShop : [];
    const meta = buildBrowseSeoMeta({
      urlSegments: segments,
      leafLabel: label,
      kind: 'picker',
      childCategoryCount: children.length || null,
      childListingTotal: children.length ? sumCategoryItemCounts(children) : null,
      pageUrl: typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined,
    });
    this.seoService.applyBrowsePage(meta);
  }

  private getBreadcrumbAliasKey(): string {
    const routeAlias = this.route.snapshot.data['breadcrumb']?.alias;
    if (routeAlias) {
      return `@${routeAlias}`;
    }
    return this.config.breadcrumbAlias;
  }

  private clearStaleBrowseFilters(): void {
    this.globals.listingFilter.location = null;
    this.globals.listingFilter.keyword = null;
    this.globals.listingFilter.minPrice = null;
    this.globals.listingFilter.maxPrice = null;
    this.globals.listingFilter.attributes = {};
    this.globals.location = null;
  }

  /**
   * Component leaves (e.g. smartphone/used) have no child picker rows. When routing lands on
   * …/used instead of …/used/all, send the user to the listing form.
   */
  private maybeRedirectAddListingLeafForm(pathOnly: string): void {
    if (this.context !== 'add-item' || pathOnly.endsWith('/all')) {
      return;
    }
    if (this.itemsShop?.length) {
      return;
    }
    const taxonomy = parseAddListingTaxonomySegments(pathOnly);
    if (taxonomy.length < 2) {
      return;
    }
    void this.router.navigateByUrl(`${pathOnly}/all`, {replaceUrl: true});
  }

  /**
   * Browse component leaves have no child picker. Redirect …/used → …/used/all.
   */
  private maybeRedirectBrowseLeafListings(pathOnly: string): void {
    if (!this.isCategoryBrowse || pathOnly.endsWith('/all')) {
      return;
    }
    if (this.itemsShop?.length) {
      return;
    }
    const segments = pathOnly.split('/').filter(Boolean);
    const offset = isProfileUrl(segments) ? 1 : 0;
    const taxonomy = segments.slice(offset).filter(segment => segment && segment !== 'all');
    if (taxonomy.length < 2) {
      return;
    }
    void this.router.navigateByUrl(`${pathOnly}/all`, {replaceUrl: true});
  }
}
